/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { useEffect, useMemo, useState } from 'react';
import { WidgetType } from '@utils/widget-registry';
import { DashboardWidgetRenderer } from '@pages/home/dashboard-widget-renderer';
import { WidgetItem } from '@/contexts/dashboard-context';
import {
    ActivityCounterConfig,
    ActivityCounterVariant,
    ShortcutConfig,
    WidgetGridSize,
    WidgetSelectionOption,
} from './home.types';
import { Activity } from '@models/time/activity';
import I18N from '@utils/i18n.base';

interface WidgetSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (option: WidgetSelectionOption) => void;
    availableWidgetTypes: WidgetType[];
}

const PREVIEW_CELL_SIZE = 96;
const PREVIEW_MAX_WIDTH = 200;
const PREVIEW_MAX_HEIGHT = 160;

function getWidgetDimensions(size: WidgetGridSize): [number, number] {
    const dimensions: Record<WidgetGridSize, [number, number]> = {
        '1x1': [1, 1],
        '1x2': [1, 2],
        '2x2': [2, 2],
        '2x4': [2, 4],
        '3x4': [3, 4],
    };
    return dimensions[size];
}

function getPreviewScale(widgetSize: WidgetGridSize) {
    const [rows, cols] = getWidgetDimensions(widgetSize);
    const actualWidth = cols * PREVIEW_CELL_SIZE;
    const actualHeight = rows * PREVIEW_CELL_SIZE;
    const scaleX = PREVIEW_MAX_WIDTH / actualWidth;
    const scaleY = PREVIEW_MAX_HEIGHT / actualHeight;
    return Math.min(1, scaleX, scaleY);
}

/**
 * Two-column modal for picking a widget variant.
 *
 * Left column lists available widget types; right column renders live previews
 * of each variant/size the selected type supports, so users can see exactly
 * what they are about to add.
 */
export function WidgetSelectorModal({ isOpen, onClose, onSelect, availableWidgetTypes }: WidgetSelectorModalProps) {
    const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSelectedTypeId(null);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && availableWidgetTypes.length > 0 && !selectedTypeId) {
            setSelectedTypeId(availableWidgetTypes[0].id);
        }
    }, [isOpen, availableWidgetTypes, selectedTypeId]);

    const selectedType = availableWidgetTypes.find((t) => t.id === selectedTypeId);

    const options = useMemo<WidgetSelectionOption[]>(() => {
        if (!selectedType) return [];

        if (selectedType.componentId === 'ClockWidget' || selectedType.name === 'clock') {
            const id = selectedType.id;
            return [
                {
                    widgetTypeId: id,
                    title: 'Digital',
                    subtitle: 'Basic digital',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'digital', scope: 'local' },
                },
                {
                    widgetTypeId: id,
                    title: 'Analog I',
                    subtitle: 'Numbered face',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'analog1', scope: 'local' },
                },
                {
                    widgetTypeId: id,
                    title: 'Analog II',
                    subtitle: 'Minimal face',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'analog2', scope: 'local' },
                },
                {
                    widgetTypeId: id,
                    title: 'Analog III',
                    subtitle: 'Utility face',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'analog3', scope: 'local' },
                },
                {
                    widgetTypeId: id,
                    title: 'City Digital',
                    subtitle: 'City name + offset',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'digital', scope: 'city', city: 'Beijing' },
                },
                {
                    widgetTypeId: id,
                    title: 'City I',
                    subtitle: 'Numbered + city',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'analog1', scope: 'city', city: 'Beijing' },
                },
                {
                    widgetTypeId: id,
                    title: 'City II',
                    subtitle: 'Minimal + city',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'analog2', scope: 'city', city: 'Beijing' },
                },
                {
                    widgetTypeId: id,
                    title: 'City III',
                    subtitle: 'Utility + city',
                    widgetSize: '2x2',
                    presetConfig: { variant: 'analog3', scope: 'city', city: 'Beijing' },
                },
                {
                    widgetTypeId: id,
                    title: 'World Clock',
                    subtitle: '4-city dashboard',
                    widgetSize: '3x4',
                    presetConfig: { variant: 'world', cities: ['Beijing', 'London', 'New York', 'Tokyo'] },
                },
                {
                    widgetTypeId: id,
                    title: 'Date & Time',
                    subtitle: 'Time, date & lunar',
                    widgetSize: '2x4',
                    presetConfig: { variant: 'datetime' },
                },
            ];
        }

        if (selectedType.componentId === 'ActivityCounterWidget' || selectedType.name === 'activity_counter') {
            return [
                {
                    widgetTypeId: selectedType.id,
                    title: I18N.activity.counterPlain,
                    subtitle: I18N.activity.counterPlainDescription,
                    widgetSize: '2x2' as const,
                    presetConfig: { variant: 'plain' },
                },
                {
                    widgetTypeId: selectedType.id,
                    title: I18N.activity.counterAccent,
                    subtitle: I18N.activity.counterAccentDescription,
                    widgetSize: '2x2' as const,
                    presetConfig: { variant: 'accent' },
                },
            ];
        }

        const supportedSizes = (selectedType.supportedSizes ?? []) as WidgetGridSize[];
        if (supportedSizes.length === 0) {
            return [
                {
                    widgetTypeId: selectedType.id,
                    title: selectedType.displayName,
                    subtitle: selectedType.name,
                },
            ];
        }

        return supportedSizes.map((size) => ({
            widgetTypeId: selectedType.id,
            title: `${selectedType.displayName} (${size})`,
            subtitle: `${size} layout`,
            widgetSize: size,
        }));
    }, [selectedType]);

    const createPreviewItem = (option: WidgetSelectionOption): WidgetItem => ({
        id: `preview-${option.widgetTypeId}-${option.widgetSize ?? 'default'}`,
        layoutId: 'preview',
        widgetTypeId: option.widgetTypeId,
        widgetType: selectedType,
        widgetSize: option.widgetSize ?? '1x1',
        gridX: 0,
        gridY: 0,
        config: option.presetConfig ?? {},
        zOrder: 0,
        isVisible: true,
        isLocked: false,
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="text-theme-text flex max-h-[85vh] w-208 max-w-[95vw] overflow-hidden rounded-xl border border-(--color-theme-border) bg-(--color-theme-popup-bg) shadow-2xl">
                {/* ── Left column: widget type list ── */}
                <div className="flex w-56 shrink-0 flex-col border-r border-(--color-theme-border-divider) bg-(--color-theme-subtle-bg) p-4">
                    <h2 className="text-theme-text mb-1 text-base font-bold">Add Widget</h2>
                    <p className="mb-3 text-xs text-(--color-theme-text-muted)">Choose a type</p>

                    <div className="flex-1 space-y-1 overflow-y-auto pr-1">
                        {availableWidgetTypes.length === 0 ? (
                            <p className="py-4 text-center text-sm text-(--color-theme-text-muted)">
                                No widget types available
                            </p>
                        ) : (
                            availableWidgetTypes.map((type) => (
                                <button
                                    key={type.id}
                                    onClick={() => setSelectedTypeId(type.id)}
                                    className={`w-full rounded-lg px-3 py-2 text-left transition-colors ${
                                        selectedTypeId === type.id
                                            ? 'bg-theme-selection text-(--color-theme-active)'
                                            : 'text-theme-text-light hover:bg-theme-hover'
                                    }`}
                                >
                                    <div className="text-sm font-medium">{type.displayName}</div>
                                    {type.description && (
                                        <div className="mt-0.5 max-h-8 overflow-hidden text-xs text-(--color-theme-text-muted)">
                                            {type.description}
                                        </div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* ── Right column: live preview options ── */}
                <div className="flex flex-1 flex-col p-5">
                    <div className="mb-4">
                        <h3 className="text-theme-text text-sm font-semibold">
                            {selectedType ? `Choose ${selectedType.displayName} variant` : 'Select a type'}
                        </h3>
                        <p className="mt-0.5 text-xs text-(--color-theme-text-muted)">
                            {selectedType
                                ? 'Click a preview to add it to your dashboard'
                                : 'Choose a widget type from the left'}
                        </p>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {!selectedType ? (
                            <div className="flex items-center justify-center py-16">
                                <p className="text-sm text-(--color-theme-text-muted)">Select a widget type first</p>
                            </div>
                        ) : options.length === 0 ? (
                            <div className="flex items-center justify-center py-16">
                                <p className="text-sm text-(--color-theme-text-muted)">No variants available</p>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-5">
                                {options.map((option) => {
                                    const previewItem = createPreviewItem(option);
                                    const [rows, cols] = getWidgetDimensions(option.widgetSize ?? '1x1');
                                    const actualWidth = cols * PREVIEW_CELL_SIZE;
                                    const actualHeight = rows * PREVIEW_CELL_SIZE;
                                    const scale = getPreviewScale(option.widgetSize ?? '1x1');

                                    return (
                                        <button
                                            key={`${option.widgetTypeId}-${option.title}`}
                                            onClick={() => {
                                                onSelect(option);
                                                onClose();
                                            }}
                                            className="group text-theme-text flex flex-col items-center rounded-xl border border-(--color-theme-border) bg-(--color-theme-card-bg) p-3 transition-all hover:border-(--color-theme-active) hover:shadow-md"
                                        >
                                            <div
                                                className="overflow-hidden rounded-2xl"
                                                style={{
                                                    width: `${Math.ceil(actualWidth * scale)}px`,
                                                    height: `${Math.ceil(actualHeight * scale)}px`,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: `${actualWidth}px`,
                                                        height: `${actualHeight}px`,
                                                        transform: `scale(${scale})`,
                                                        transformOrigin: 'top left',
                                                    }}
                                                >
                                                    <DashboardWidgetRenderer
                                                        item={previewItem}
                                                        isEditMode={false}
                                                        isFrozen
                                                    />
                                                </div>
                                            </div>
                                            <div className="mt-2.5 text-center">
                                                <div className="text-theme-text-light text-xs font-medium group-hover:text-(--color-theme-active)">
                                                    {option.title}
                                                </div>
                                                <div className="text-xs text-(--color-theme-text-muted)">
                                                    {option.subtitle}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex justify-end border-t border-(--color-theme-border-divider) pt-3">
                        <button
                            onClick={onClose}
                            className="text-theme-text-light hover:bg-theme-hover rounded-lg border border-(--color-theme-border) px-4 py-2 text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface ActivityCounterSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (payload: ActivityCounterConfig) => void;
    heading: string;
    confirmLabel: string;
    initialActivityId: string;
    initialVariant: ActivityCounterVariant;
}

function canUseActivityCounter(activity: Activity) {
    return (
        activity.counterMode !== 'none' &&
        activity.timings.some(
            (timing) => timing.rangeType !== 'none' && Boolean(timing.startTime?.date ?? timing.endTime?.date)
        )
    );
}

export function ActivityCounterSetupModal({
    isOpen,
    onClose,
    onConfirm,
    heading,
    confirmLabel,
    initialActivityId,
    initialVariant,
}: ActivityCounterSetupModalProps) {
    const [activities, setActivities] = useState<Activity[]>([]);
    const [activityId, setActivityId] = useState(initialActivityId);
    const [variant, setVariant] = useState<ActivityCounterVariant>(initialVariant);
    const [isLoading, setIsLoading] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let active = true;
        setActivityId(initialActivityId);
        setVariant(initialVariant);
        setIsLoading(true);
        setLoadFailed(false);

        void Activity.listAll({ sortBy: 'updatedAt', sortOrder: 'desc' })
            .then((items) => {
                if (!active) return;
                const eligible = items.filter(canUseActivityCounter);
                setActivities(eligible);
                setActivityId((current) =>
                    eligible.some((activity) => activity.id === current) ? current : (eligible[0]?.id ?? '')
                );
            })
            .catch(() => {
                if (!active) return;
                setActivities([]);
                setActivityId('');
                setLoadFailed(true);
            })
            .finally(() => active && setIsLoading(false));

        return () => {
            active = false;
        };
    }, [initialActivityId, initialVariant, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="text-theme-text w-md max-w-[92vw] rounded-xl border border-(--color-theme-border) bg-(--color-theme-popup-bg) p-5 shadow-2xl">
                <div className="mb-5">
                    <h2 className="text-base font-semibold">{heading}</h2>
                    <p className="text-theme-text-light mt-1 text-sm">{I18N.activity.counterWidgetDescription}</p>
                </div>

                <div className="space-y-5">
                    <label className="block">
                        <span className="mb-2 block text-sm font-medium">{I18N.activity.chooseCounterActivity}</span>
                        <select
                            value={activityId}
                            onChange={(event) => setActivityId(event.target.value)}
                            disabled={isLoading || activities.length === 0}
                            className="text-theme-text border-theme-input-border bg-theme-input focus:border-theme-focus h-10 w-full rounded-lg border px-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {activities.length === 0 && (
                                <option value="">
                                    {isLoading
                                        ? I18N.activity.loadingCounterActivities
                                        : loadFailed
                                          ? I18N.activity.failedLoadCounterActivities
                                          : I18N.activity.noCounterActivities}
                                </option>
                            )}
                            {activities.map((activity) => (
                                <option key={activity.id} value={activity.id}>
                                    {activity.name} -{' '}
                                    {activity.counterMode === 'countdown'
                                        ? I18N.activity.daysUntil
                                        : I18N.activity.daysSince}
                                </option>
                            ))}
                        </select>
                    </label>

                    <fieldset>
                        <legend className="mb-2 text-sm font-medium">{I18N.activity.counterAppearance}</legend>
                        <div className="grid grid-cols-2 gap-2">
                            {(['plain', 'accent'] as ActivityCounterVariant[]).map((option) => (
                                <label
                                    key={option}
                                    className="border-theme-border hover:bg-theme-hover flex cursor-pointer items-start gap-2.5 rounded-lg border p-3"
                                >
                                    <input
                                        type="radio"
                                        name="activity-counter-variant"
                                        value={option}
                                        checked={variant === option}
                                        onChange={() => setVariant(option)}
                                        className="mt-0.5 h-4 w-4 accent-(--color-theme-active)"
                                    />
                                    <span className="min-w-0">
                                        <span className="block text-sm font-medium">
                                            {option === 'plain'
                                                ? I18N.activity.counterPlain
                                                : I18N.activity.counterAccent}
                                        </span>
                                        <span className="text-theme-text-muted mt-0.5 block text-xs">
                                            {option === 'plain'
                                                ? I18N.activity.counterPlainDescription
                                                : I18N.activity.counterAccentDescription}
                                        </span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    </fieldset>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-theme-text-light hover:bg-theme-hover rounded-lg border border-(--color-theme-border) px-4 py-2 text-sm"
                    >
                        {I18N.app.cancel}
                    </button>
                    <button
                        type="button"
                        disabled={!activityId || isLoading}
                        onClick={() => onConfirm({ activityId, variant })}
                        className="text-theme-active-contrast rounded-lg bg-(--color-theme-active) px-4 py-2 text-sm font-medium hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

interface ShortcutSetupModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (payload: ShortcutConfig) => void;
    heading: string;
    initialTitle: string;
    initialUrl: string;
    confirmLabel: string;
    description: string;
}

/**
 * Dedicated shortcut editor so add/edit flows share the same validation and layout.
 */
export function ShortcutSetupModal({
    isOpen,
    onClose,
    onConfirm,
    heading,
    initialTitle,
    initialUrl,
    confirmLabel,
    description,
}: ShortcutSetupModalProps) {
    const [title, setTitle] = useState(initialTitle);
    const [url, setUrl] = useState(initialUrl);

    useEffect(() => {
        if (!isOpen) return;
        setTitle(initialTitle);
        setUrl(initialUrl);
    }, [isOpen, initialTitle, initialUrl]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="text-theme-text w-md max-w-[92vw] rounded-xl border border-(--color-theme-border) bg-(--color-theme-popup-bg) p-5 shadow-2xl">
                <div className="text-theme-text mb-1 text-lg font-semibold">{heading}</div>
                <div className="text-theme-text-light mb-4 text-sm">{description}</div>

                <div className="space-y-3">
                    <input
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="Title (optional)"
                        className="text-theme-text w-full rounded-lg border border-(--color-theme-input-border) bg-(--color-theme-input) px-3 py-2 text-sm outline-none focus:border-(--color-theme-focus)"
                    />
                    <input
                        value={url}
                        onChange={(event) => setUrl(event.target.value)}
                        placeholder="https://example.com"
                        className="text-theme-text w-full rounded-lg border border-(--color-theme-input-border) bg-(--color-theme-input) px-3 py-2 text-sm outline-none focus:border-(--color-theme-focus)"
                    />
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="text-theme-text-light hover:bg-theme-hover rounded-lg border border-(--color-theme-border) px-4 py-2 text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onConfirm({ title: title.trim(), url: url.trim() })}
                        className="text-theme-active-contrast rounded-lg bg-(--color-theme-active) px-4 py-2 text-sm font-medium hover:opacity-90"
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}
